import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PlaybookStatusBadge from './PlaybookStatusBadge';
import StuckBanner from './StuckBanner';
import TransitionButton from './TransitionButton';
import TransitionExecutionModal from './TransitionExecutionModal';
import ForceTransitionModal from './ForceTransitionModal';
import PlaybookTimeline from './PlaybookTimeline';
import DiagnosticsPanel from './DiagnosticsPanel';
import ApprovalStepper from './ApprovalStepper';
import ApprovalBadge from './ApprovalBadge';
import AdminInstanceActions from './AdminInstanceActions';
import { usePlaybookTransitions } from '../hooks/usePlaybookTransitions';
import { playbookApi } from '../api/playbookApi';
import type {
  PlaybookInstance,
  AvailableTransition,
  PlaybookApproval,
  PlaybookStatusCategory,
  ExecuteTransitionResponse,
} from '../types/playbook.types';

interface PlaybookInstanceCardProps {
  instance: PlaybookInstance;
  isAdmin: boolean;
  currentUserId: string;
  onRefresh: () => void;
}

type SubTab = 'transitions' | 'timeline' | 'diagnostics';

const GRADIENT_BY_CATEGORY: Record<string, string> = {
  todo: 'from-gray-400 via-gray-300 to-gray-200',
  in_progress: 'from-lex-accent to-lex-accent-hover',
  done: 'from-emerald-500 via-teal-400 to-cyan-400',
  blocked: 'from-rose-500 via-rose-400 to-pink-400',
};

function useSlidingIndicator(activeTab: SubTab) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const el = tabRefs.current[activeTab];
    const container = containerRef.current;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      setIndicator({
        left: elRect.left - containerRect.left,
        width: elRect.width,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const setRef = useCallback(
    (id: string) => (el: HTMLButtonElement | null) => {
      tabRefs.current[id] = el;
    },
    [],
  );

  return { indicator, setRef, containerRef };
}

const PlaybookInstanceCard: React.FC<PlaybookInstanceCardProps> = ({
  instance,
  isAdmin,
  currentUserId,
  onRefresh,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('transitions');
  const [selectedTransition, setSelectedTransition] = useState<AvailableTransition | null>(null);
  const [forceTransition, setForceTransition] = useState<AvailableTransition | null>(null);
  const [approvals, setApprovals] = useState<Record<string, PlaybookApproval[]>>({});
  const [approvalsLoading, setApprovalsLoading] = useState<Record<string, boolean>>({});
  const [contentVisible, setContentVisible] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  const { indicator, setRef, containerRef } = useSlidingIndicator(activeSubTab);
  const { transitions, loading: transitionsLoading, refresh: refreshTransitions } = usePlaybookTransitions(instance.id);

  const statusCategory: PlaybookStatusCategory | 'blocked' = instance.error_state
    ? 'blocked'
    : instance.currentStatus?.category || 'todo';

  const gradient = GRADIENT_BY_CATEGORY[statusCategory] || GRADIENT_BY_CATEGORY.todo;

  // Progress calculation
  const totalStatuses = instance.playbook?.statuses?.length || 0;
  const currentSortOrder = instance.currentStatus?.sort_order ?? 0;
  const progressPercent = totalStatuses > 0 ? Math.round(((currentSortOrder + 1) / totalStatuses) * 100) : 0;

  // Pending approval count
  const pendingApprovalCount = useMemo(() => {
    return Object.values(approvals).reduce(
      (sum, steps) => sum + steps.filter((s) => s.status === 'pending').length,
      0,
    );
  }, [approvals]);

  // Load approvals for collaborative transitions
  const loadApprovals = useCallback(async (transitionId: string) => {
    setApprovalsLoading((prev) => ({ ...prev, [transitionId]: true }));
    try {
      const res = await playbookApi.getApprovalStatus(instance.id, transitionId);
      const data = res as any;
      setApprovals((prev) => ({ ...prev, [transitionId]: Array.isArray(data) ? data : [] }));
    } catch {
      // Silent fail — approvals are optional
    } finally {
      setApprovalsLoading((prev) => ({ ...prev, [transitionId]: false }));
    }
  }, [instance.id]);

  useEffect(() => {
    transitions
      .filter((t) => t.isCollaborative)
      .forEach((t) => loadApprovals(t.transitionId));
  }, [transitions, loadApprovals]);

  const handleTransitionComplete = useCallback(
    (_result: ExecuteTransitionResponse) => {
      setSelectedTransition(null);
      refreshTransitions();
      onRefresh();
    },
    [refreshTransitions, onRefresh],
  );

  const handleForceComplete = useCallback(
    (_result: ExecuteTransitionResponse) => {
      setForceTransition(null);
      refreshTransitions();
      onRefresh();
    },
    [refreshTransitions, onRefresh],
  );

  const handleApprovalDecision = useCallback(
    async (transitionId: string, decision: 'approved' | 'rejected', comment?: string) => {
      try {
        await playbookApi.approveTransition(instance.id, transitionId, { decision, comment });
        loadApprovals(transitionId);
        refreshTransitions();
        onRefresh();
      } catch (err) {
        console.error('[PlaybookInstanceCard] Approval failed:', err);
      }
    },
    [instance.id, loadApprovals, refreshTransitions, onRefresh],
  );

  // Crossfade tab switching
  const switchTab = useCallback((tab: SubTab) => {
    setContentVisible(false);
    setTimeout(() => {
      setActiveSubTab(tab);
      setRenderKey((k) => k + 1);
      requestAnimationFrame(() => setContentVisible(true));
    }, 180);
  }, []);

  const SUB_TABS: Array<{ id: SubTab; label: string }> = [
    { id: 'transitions', label: 'Transitions' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'diagnostics', label: 'Diagnostics' },
  ];

  return (
    <>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        {/* Gradient accent */}
        <div className={`h-1 bg-gradient-to-r ${gradient}`} />

        {/* Header */}
        <div className="px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {instance.playbook?.name || 'Playbook'}
              </h3>
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                v{instance.playbook_version}
              </span>
              <PlaybookStatusBadge
                category={statusCategory}
                name={instance.currentStatus?.name || 'Unknown'}
              />
              {pendingApprovalCount > 0 && <ApprovalBadge count={pendingApprovalCount} />}
            </div>

            {/* Progress indicator */}
            {totalStatuses > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Status {currentSortOrder + 1} of {totalStatuses}
                </span>
                <div className="w-20 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700">
                  <div
                    className="h-1.5 rounded-full bg-lex-accent transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Started / Completed dates */}
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span>Started {new Date(instance.started_at).toLocaleDateString()}</span>
            {instance.completed_at && (
              <span>Completed {new Date(instance.completed_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        {/* Error banners */}
        {instance.error_state && (
          <div className="px-4 pb-3">
            <StuckBanner
              instance={instance}
              isAdmin={isAdmin}
              onViewDiagnostics={() => switchTab('diagnostics')}
              onForceNext={() => {
                const firstBlocked = transitions.find((t) => !t.isAllowed);
                if (firstBlocked) setForceTransition(firstBlocked);
              }}
            />
          </div>
        )}

        {/* Sub-tab navigation */}
        <div className="border-t border-gray-100 dark:border-gray-700">
          <div ref={containerRef} className="relative flex bg-gray-50 dark:bg-gray-900/50">
            {/* Sliding indicator */}
            <div
              className="absolute bottom-0 h-0.5 bg-lex-accent transition-all duration-300"
              style={{
                left: indicator.left,
                width: indicator.width,
                transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
              }}
            />
            {SUB_TABS.map((tab) => (
              <button
                key={tab.id}
                ref={setRef(tab.id)}
                onClick={() => switchTab(tab.id)}
                className={`px-4 py-2 text-xs font-medium transition-colors ${
                  activeSubTab === tab.id
                    ? 'text-lex-accent'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sub-tab content */}
        <div
          key={renderKey}
          className="px-4 py-3"
          style={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? 'translateY(0)' : 'translateY(6px)',
            transition: 'opacity 180ms ease-out, transform 180ms ease-out',
          }}
        >
          {activeSubTab === 'transitions' && (
            <div className="space-y-3">
              {transitionsLoading ? (
                <div className="flex items-center py-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading transitions...</span>
                </div>
              ) : transitions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {instance.completed_at ? 'Workflow complete.' : 'No transitions available from this status.'}
                </p>
              ) : (
                <>
                  {/* Transition buttons */}
                  <div className="flex flex-wrap gap-2">
                    {transitions.filter((t) => !t.isCollaborative || !approvals[t.transitionId]).map((t) => (
                      <TransitionButton
                        key={t.transitionId}
                        transition={t}
                        isAdmin={isAdmin}
                        onExecute={(tid) => {
                          const tr = transitions.find((x) => x.transitionId === tid);
                          if (tr) setSelectedTransition(tr);
                        }}
                        onForce={(tid) => {
                          const tr = transitions.find((x) => x.transitionId === tid);
                          if (tr) setForceTransition(tr);
                        }}
                      />
                    ))}
                  </div>

                  {/* Collaborative transitions with approval steppers */}
                  {transitions
                    .filter((t) => t.isCollaborative && approvals[t.transitionId])
                    .map((t) => (
                      <div key={t.transitionId} className="rounded-lg border border-lex-accent p-3 bg-lex-accent-soft">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{t.name}</span>
                          <span className="inline-flex items-center rounded-full bg-lex-accent-soft px-2 py-0.5 text-[10px] font-medium text-lex-accent">
                            Pending Approval
                          </span>
                        </div>
                        <ApprovalStepper
                          approvals={approvals[t.transitionId]}
                          currentUserId={currentUserId}
                          onApprove={(decision, comment) =>
                            handleApprovalDecision(t.transitionId, decision, comment)
                          }
                          loading={approvalsLoading[t.transitionId]}
                        />
                      </div>
                    ))}
                </>
              )}
            </div>
          )}

          {activeSubTab === 'timeline' && (
            <PlaybookTimeline instanceId={instance.id} />
          )}

          {activeSubTab === 'diagnostics' && (
            <DiagnosticsPanel instanceId={instance.id} />
          )}
        </div>

        {/* Admin actions */}
        {isAdmin && (
          <div className="px-4 pb-3">
            <AdminInstanceActions
              instanceId={instance.id}
              instance={instance}
              onUpdate={onRefresh}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedTransition && (
        <TransitionExecutionModal
          isOpen={!!selectedTransition}
          instanceId={instance.id}
          transition={selectedTransition}
          onClose={() => setSelectedTransition(null)}
          onComplete={handleTransitionComplete}
        />
      )}
      {forceTransition && (
        <ForceTransitionModal
          isOpen={!!forceTransition}
          instanceId={instance.id}
          transition={forceTransition}
          onClose={() => setForceTransition(null)}
          onComplete={handleForceComplete}
        />
      )}
    </>
  );
};

export default PlaybookInstanceCard;
